import React from 'react';
import OutsideClickHandler from 'components/outside-click-handler';
import {
  Arrow,
  Container,
  DropdownContainer,
  DropdownMenu,
  OptionLabel,
  OptionWrapper,
  SelectedContainer,
} from './style';
import { pick, uniq } from 'lodash';
import { SurfaceRole } from 'graphql/types';
import useSurfaceRolesMap, {
  SurfaceRolesMap,
} from 'routes/surface/hooks/use-surface-roles-map';

export interface DropdownProps {
  value?: any;
  defaultValue?: any;
  options?: string[];
  onChange?: (item: Role) => void;
  OptionWrapper?: any;
}

export interface Role {
  label: string;
  value: any;
}

const defaultProps = {
  OptionWrapper: React.Fragment,
};

function SurfaceRolesDropdown(props: DropdownProps) {
  const surfaceRolesMap = useSurfaceRolesMap();

  const [isDropdownVisible, setDropdownVisible] = React.useState<boolean>(
    false,
  );

  const options = React.useMemo<{ value: SurfaceRole; label: string }[]>(
    () =>
      // @ts-ignore
      Object.entries<SurfaceRolesMap>(surfaceRolesMap)
        .reduce<{ value: SurfaceRole; label: string }[]>(
          (options, [value, label]) => [...options, { value, label }],
          [],
        )
        .filter(({ value }) => value !== SurfaceRole.Owner),
    [],
  );

  const [selected, setSelected] = React.useState<Role | undefined>(
    options.find((data) => data.value === props.value),
  );

  const styleProps = pick(props, ['style', 'className']);

  React.useEffect(() => {
    const defaultValue = options.find(
      (data) => data.value === props.defaultValue,
    );

    if (defaultValue) {
      setSelected(defaultValue);
      props.onChange && props.onChange(defaultValue);
    }

    return () => setDropdownVisible(false);
  }, []);

  function toggleDropdown() {
    setDropdownVisible(!isDropdownVisible);
  }

  function onChange(item: Role): Function {
    return function () {
      setSelected(item);
      setDropdownVisible(false);
      props.onChange && props.onChange(item);
    };
  }

  const OptionWrapperComponent = props.OptionWrapper ?? React.Fragment;

  return (
    <OutsideClickHandler
      onClick={() => {
        if (isDropdownVisible) {
          setDropdownVisible(false);
        }
      }}>
      <Container {...styleProps}>
        <SelectedContainer onClick={toggleDropdown}>
          <OptionWrapperComponent>
            {selected && <Option item={selected} isSelected />}
            <Arrow $menuOpened={isDropdownVisible} />
          </OptionWrapperComponent>
        </SelectedContainer>

        {isDropdownVisible && (
          <DropdownContainer>
            <DropdownMenu>
              {uniq(options).map((item) => (
                <OptionWrapper key={item.value} onClick={onChange(item)}>
                  <Option
                    item={item}
                    isSelected={selected?.value === item.value}
                  />
                </OptionWrapper>
              ))}
            </DropdownMenu>
          </DropdownContainer>
        )}
      </Container>
    </OutsideClickHandler>
  );
}

function Option({ item, isSelected }) {
  return <OptionLabel $isSelected={isSelected}>{item.label}</OptionLabel>;
}

SurfaceRolesDropdown.defaultProps = defaultProps;

export default SurfaceRolesDropdown;
